import { useEffect, useState } from "react";

/**
 * FinAI by Revurge branded loader component
 * Displays an animated loading state with the FinAI branding
 */
export default function FinAILoader({ 
  fullScreen = true, 
  message = "Loading...",
  showBranding = true,
  size = "default" // "small", "default", "large"
}) {
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? "" : prev + ".");
    }, 400);
    return () => clearInterval(interval);
  }, []);

  const sizeClasses = {
    small: { logo: "w-8 h-8", text: "text-sm", spinner: "w-6 h-6" },
    default: { logo: "w-12 h-12", text: "text-base", spinner: "w-8 h-8" },
    large: { logo: "w-16 h-16", text: "text-lg", spinner: "w-12 h-12" }
  };

  const sizes = sizeClasses[size] || sizeClasses.default;

  const content = (
    <div className="flex flex-col items-center justify-center gap-6">
      {/* Animated Logo */}
      <div className="relative">
        {/* Outer glow ring */}
        <div className="absolute inset-0 animate-ping-slow">
          <div className={`${sizes.logo} rounded-2xl bg-[#BFFF00]/30`} />
        </div>
        
        {/* Rotating ring */}
        <div className="absolute -inset-2">
          <svg className={`${sizes.logo === "w-16 h-16" ? "w-20 h-20" : sizes.logo === "w-12 h-12" ? "w-16 h-16" : "w-12 h-12"} animate-spin-slow`} viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="#BFFF00"
              strokeWidth="2"
              strokeDasharray="70 200"
              strokeLinecap="round"
            />
          </svg>
        </div>
        
        {/* Main logo */}
        <div className={`relative ${sizes.logo} bg-[#BFFF00] rounded-2xl flex items-center justify-center shadow-lg shadow-[#BFFF00]/30 animate-pulse-subtle`}>
          <span className={`text-[#121212] font-extrabold ${size === "large" ? "text-3xl" : size === "small" ? "text-lg" : "text-2xl"}`}>F</span>
        </div>
      </div>

      {/* Branding text */}
      {showBranding && (
        <div className="text-center">
          <h2 className={`font-bold text-[#121212] font-['Manrope'] ${size === "large" ? "text-2xl" : size === "small" ? "text-base" : "text-xl"}`}>
            FinAI
          </h2>
          <p className="text-xs text-[#666] tracking-wider">by revurge</p>
        </div>
      )}

      {/* Loading bar */}
      <div className="w-48 h-1 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#BFFF00] via-[#a8e600] to-[#BFFF00] rounded-full animate-loading-bar" />
      </div>

      {/* Message */}
      <p className={`text-[#666] ${sizes.text} min-w-[120px] text-center`}>
        {message}<span className="inline-block w-6 text-left">{dots}</span>
      </p>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 bg-[#F7FFE5] flex items-center justify-center">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-20" style={{ 
          backgroundImage: 'linear-gradient(#BFFF00 1px, transparent 1px), linear-gradient(90deg, #BFFF00 1px, transparent 1px)', 
          backgroundSize: '40px 40px' 
        }} />
        
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-[#BFFF00] rounded-full animate-float-particle"
              style={{
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.5}s`,
                opacity: 0.4
              }}
            />
          ))}
        </div>
        
        <div className="relative z-10">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-8">
      {content}
    </div>
  );
}

/**
 * Inline loader for buttons or small spaces
 */
export function FinAISpinner({ className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeDasharray="30 70"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}
